/**
 * *New map* — `docs/property-maps-v2.md` §5.6, board 5.
 *
 * Name, where to start from, and whether to add a first layer. Three
 * decisions, one dialog, and the third is ticked by default because a map
 * with no layers is a map you cannot draw on — the *Create a map* button
 * should not hand somebody an empty canvas and a second thing to do.
 *
 * **A copy of another map copies its layers and their audiences**, which is
 * the point: last winter's snow plan is the right starting place for this
 * winter's, and re-picking who sees each layer is the part nobody would do.
 */
import { useState } from 'react'
import { Button, Checkbox } from '../../components/primitives.js'
import { SheetOrDialog } from '../../components/sheet-or-dialog.js'
import { Field, SelectInput, TextInput } from '../../components/form/index.js'
import type { MapSummary } from '../api.js'

export function NewMapDialog({
  propertyName, maps, onCancel, onCreate,
}: {
  propertyName: string
  /** The maps that could be copied. Empty disables that choice. */
  maps: MapSummary[]
  onCancel: () => void
  onCreate: (body: {
    name: string; from: 'blank' | { copyMapId: string }; firstLayer: boolean
  }) => Promise<void>
}) {
  const [name, setName] = useState(`${propertyName} map`)
  const [copyFrom, setCopyFrom] = useState<string>('')
  const [firstLayer, setFirstLayer] = useState(true)
  const [busy, setBusy] = useState(false)

  const ready = name.trim().length > 0 && !busy

  return (
    <SheetOrDialog
      onClose={onCancel}
      title="New map"
      sub={propertyName}
      width={480}
      footerNote="Not shared with customers."
      footer={(
        <>
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ready}
            onClick={() => {
              setBusy(true)
              void onCreate({
                name: name.trim(),
                from: copyFrom ? { copyMapId: copyFrom } : 'blank',
                firstLayer,
              }).finally(() => setBusy(false))
            }}
          >
            {busy ? 'Creating…' : 'Create map'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-4 px-5 py-4">
        <Field label="Name" required>
          {({ id }) => (
            <TextInput
              id={id}
              value={name}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              placeholder="Snow removal 2026–27"
            />
          )}
        </Field>

        <Field
          label="Start from"
          hint={maps.length === 0 ? 'There is no other map on this property yet.' : undefined}
        >
          {({ id }) => (
            <SelectInput
              id={id}
              label="Start from"
              value={copyFrom}
              onChange={(value) => setCopyFrom(value ?? '')}
              disabled={maps.length === 0}
              options={[
                { value: '', label: 'A blank aerial' },
                ...maps.map((one) => ({ value: one.id, label: `A copy of ${one.name}` })),
              ]}
            />
          )}
        </Field>

        <Checkbox
          checked={firstLayer}
          onChange={(event) => setFirstLayer(event.target.checked)}
          align="start"
          label={(
            <span className="flex flex-col gap-0.5">
              <b>Add a first layer for me</b>
              <span className="text-text-muted">
                Layer 1 · All crews · you can rename or narrow it any time.
              </span>
            </span>
          )}
        />
      </div>
    </SheetOrDialog>
  )
}
